import { useState } from 'react';
import {
  formatNir,
  isValidNir,
  normalizeNir,
} from '@aldebaran/common/util/nir';

import { Input } from '@/components/ui/input';

import { FieldHelper, FieldLabel } from './FieldHelper';

const NIR_LENGTH = 15;

/** French error message for a NIR, or undefined when it is empty or valid. */
export const getNirError = (value: string | null | undefined) => {
  if (!value) return undefined;
  if (normalizeNir(value).length !== NIR_LENGTH)
    return 'Le numéro de sécurité sociale doit contenir 15 caractères';
  if (!isValidNir(value)) return 'Numéro de sécurité sociale invalide';
  return undefined;
};

type NirFieldProps = {
  // Normalized NIR (no spaces), or null when empty.
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  // Parent-driven error, e.g. "required" on submit. Takes precedence.
  error?: string;
  optional?: boolean;
  // False when someone fills the form for the patient (interlocuter OTHER).
  isSelf?: boolean;
  // Reports the format error shown on blur.
  onLocalErrorChange?: (error: string | undefined) => void;
};

export const NirField = ({
  value,
  onChange,
  label = 'N° de sécurité sociale',
  error,
  optional,
  isSelf = true,
  onLocalErrorChange,
}: NirFieldProps) => {
  const [formatError, setFormatErrorState] = useState<string>();
  const setFormatError = (next: string | undefined) => {
    setFormatErrorState(next);
    onLocalErrorChange?.(next);
  };
  const displayedError = error ?? formatError;

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel optional={optional}>{label}</FieldLabel>
      <Input
        aria-label={label}
        inputMode="text"
        autoComplete="off"
        placeholder="1 85 05 78 006 084 91"
        value={formatNir(value ?? '')}
        aria-invalid={!!displayedError || undefined}
        onChange={(event) => {
          // Digits only, plus A/B for Corsica departments.
          const next = normalizeNir(event.target.value)
            .replace(/[^0-9AB]/g, '')
            .slice(0, NIR_LENGTH);
          onChange(next || null);
          setFormatError(undefined);
        }}
        onBlur={() => setFormatError(getNirError(value))}
      />
      <FieldHelper error={!!displayedError}>
        {displayedError ??
          (isSelf
            ? 'Les 15 chiffres de votre carte Vitale'
            : 'Les 15 chiffres de la carte Vitale du patient')}
      </FieldHelper>
    </div>
  );
};
